import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Button, TextField, IconButton, Menu, MenuItem, Chip, Collapse,
  InputAdornment, Paper, Stack, Tooltip, Divider, useTheme, alpha,
} from '@mui/material';
import {
  Add, MoreVert, Edit, Delete, Search, ExpandMore, Category as CategoryIcon, SubdirectoryArrowRight, FolderOpen,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { elegantMenuProps } from '../../../utils/menuStyles';
import { useModulePermissions } from '../../../hooks/useModulePermissions';
import ConfirmationDialog from '../../common/ConfirmationDialog';
import { useChart } from '../../finance/ledger/useChart';
import CategoryDialog from './CategoryDialog';
import SubCategoryDialog from './SubCategoryDialog';
import { CategoryTreeConfig, TreeCategory, TreeSubCategory } from './types';

type Target = { type: 'category'; item: TreeCategory } | { type: 'sub'; item: TreeSubCategory; parentCatId: string };

/**
 * A two-level category tree (expense or income): click a category to see
 * its sub-categories. Each category shows the ledger account it posts to.
 */
const CategoryTreeSettings: React.FC<{ config: CategoryTreeConfig }> = ({ config }) => {
  const theme = useTheme();
  const accent = theme.palette[config.accent].main;
  const { enqueueSnackbar } = useSnackbar();
  const { canRead, canWrite } = useModulePermissions();
  const hasWrite = canWrite(config.permission);
  const seesLedger = canRead('finance_accounts');
  const { accounts } = useChart(seesLedger);
  const accountName = useMemo(() => new Map(accounts.map(a => [a.code, a.name])), [accounts]);

  const [categories, setCategories] = useState<TreeCategory[]>([]);
  const [subCategories, setSubCategories] = useState<Record<string, TreeSubCategory[]>>({});
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCat, setExpandedCat] = useState<string | null>(null);
  const [catDialog, setCatDialog] = useState<{ editing: TreeCategory | null } | null>(null);
  const [subDialog, setSubDialog] = useState<{ categoryId: string; editing: TreeSubCategory | null } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Target | null>(null);
  const [menu, setMenu] = useState<{ anchor: HTMLElement; target: Target } | null>(null);

  const loadCategories = useCallback(async () => {
    try {
      setCategories(await config.service.getCategories({ search: searchQuery || undefined }));
    } catch (error: any) {
      enqueueSnackbar(error.message || 'Failed to load categories', { variant: 'error' });
    }
  }, [config.service, searchQuery, enqueueSnackbar]);

  useEffect(() => { loadCategories(); }, [loadCategories]);

  const loadSubCategories = async (categoryId: string) => {
    try {
      const subs = await config.service.getSubCategories(categoryId);
      setSubCategories(prev => ({ ...prev, [categoryId]: subs }));
    } catch (error: any) {
      enqueueSnackbar(error.message || 'Failed to load sub-categories', { variant: 'error' });
    }
  };

  const toggle = (catId: string) => {
    setExpandedCat(prev => (prev === catId ? null : catId));
    if (expandedCat !== catId && !subCategories[catId]) loadSubCategories(catId);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      if (deleteTarget.type === 'category') {
        await config.service.deleteCategory(deleteTarget.item._id);
        enqueueSnackbar('Category deleted', { variant: 'success' });
        loadCategories();
      } else {
        await config.service.deleteSubCategory(deleteTarget.parentCatId, deleteTarget.item._id);
        enqueueSnackbar('Sub-category deleted', { variant: 'success' });
        loadSubCategories(deleteTarget.parentCatId);
      }
      setDeleteTarget(null);
    } catch (error: any) {
      enqueueSnackbar(error.message || 'Failed to delete', { variant: 'error' });
    }
  };

  const openMenu = (e: React.MouseEvent<HTMLElement>, target: Target) => {
    e.stopPropagation();
    setMenu({ anchor: e.currentTarget, target });
  };

  /** "Posts to 5220 · Utilities", or the suggestion when none is chosen. */
  const postsTo = (cat: TreeCategory) => {
    const code = cat.glAccountCode || cat.suggestedAccountCode;
    if (!seesLedger || !code) return null;
    return (
      <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', mt: 0.25 }}>
        {cat.glAccountCode ? 'Posts to' : 'Posts to (suggested)'}{' '}
        <Box component="span" sx={{ fontFamily: 'monospace', color: 'text.primary' }}>{code}</Box>
        {accountName.get(code) && ` · ${accountName.get(code)}`}
      </Typography>
    );
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 3, flexWrap: 'wrap' }}>
        <TextField
          placeholder="Search categories..."
          size="small"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" sx={{ color: 'text.secondary' }} /></InputAdornment> }}
          sx={{ flexGrow: 1, maxWidth: 400 }}
        />
        {hasWrite && (
          <Button variant="contained" color={config.accent} startIcon={<Add />} onClick={() => setCatDialog({ editing: null })} sx={{ fontWeight: 600 }}>
            Add Category
          </Button>
        )}
      </Box>

      {categories.length === 0 ? (
        <Paper elevation={0} sx={{ textAlign: 'center', py: 8, bgcolor: alpha(accent, 0.02), border: `1px dashed ${alpha(accent, 0.3)}`, borderRadius: 2 }}>
          <FolderOpen sx={{ fontSize: 48, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>No {config.noun} categories yet</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {searchQuery ? 'Try adjusting your search query' : `Create your first category to organize ${config.records}`}
          </Typography>
          {hasWrite && !searchQuery && (
            <Button variant="outlined" color={config.accent} startIcon={<Add />} onClick={() => setCatDialog({ editing: null })}>Create Category</Button>
          )}
        </Paper>
      ) : (
        <Stack spacing={1.5}>
          {categories.map(cat => {
            const isExpanded = expandedCat === cat._id;
            const subs = subCategories[cat._id] || [];
            return (
              <Paper
                key={cat._id}
                variant="outlined"
                sx={{
                  borderRadius: 2, overflow: 'hidden', transition: 'all 0.2s ease',
                  borderColor: isExpanded ? alpha(accent, 0.4) : 'divider',
                  '&:hover': { borderColor: alpha(accent, 0.3), boxShadow: `0 2px 8px ${alpha(accent, 0.08)}` },
                }}
              >
                <Box
                  onClick={() => toggle(cat._id)}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 1.5, px: 2.5, py: 2, cursor: 'pointer',
                    bgcolor: isExpanded ? alpha(accent, 0.04) : 'transparent', transition: 'background-color 0.2s',
                    '&:hover': { bgcolor: alpha(accent, 0.04) },
                  }}
                >
                  <Box sx={{ width: 36, height: 36, borderRadius: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: alpha(accent, 0.1), color: accent, flexShrink: 0 }}>
                    <CategoryIcon fontSize="small" />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="subtitle1" fontWeight={600} noWrap>{cat.name}</Typography>
                      {subCategories[cat._id] !== undefined && (
                        <Chip
                          label={`${subs.length} sub`}
                          size="small"
                          sx={{
                            height: 22, fontSize: '0.7rem', fontWeight: 600,
                            bgcolor: subs.length ? alpha(accent, 0.1) : alpha(theme.palette.grey[500], 0.1),
                            color: subs.length ? accent : theme.palette.text.secondary,
                          }}
                        />
                      )}
                    </Box>
                    {cat.description && <Typography variant="body2" color="text.secondary" noWrap sx={{ mt: 0.25 }}>{cat.description}</Typography>}
                    {postsTo(cat)}
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {hasWrite && (
                      <IconButton size="small" onClick={e => openMenu(e, { type: 'category', item: cat })}
                        sx={{ color: 'text.secondary', '&:hover': { bgcolor: alpha(accent, 0.1), color: accent } }}>
                        <MoreVert fontSize="small" />
                      </IconButton>
                    )}
                    <Box sx={{ color: 'text.secondary', display: 'flex', transition: 'transform 0.2s', transform: isExpanded ? 'rotate(180deg)' : 'none' }}>
                      <ExpandMore fontSize="small" />
                    </Box>
                  </Box>
                </Box>

                <Collapse in={isExpanded}>
                  <Divider />
                  <Box sx={{ px: 2.5, py: 2, bgcolor: alpha(theme.palette.background.default, 0.5) }}>
                    {hasWrite && (
                      <Button size="small" color={config.accent} startIcon={<Add />} onClick={() => setSubDialog({ categoryId: cat._id, editing: null })}
                        sx={{ mb: subs.length ? 1.5 : 0, fontWeight: 500 }}>
                        Add Sub-Category
                      </Button>
                    )}
                    {subs.length > 0 ? (
                      <Stack spacing={1}>
                        {subs.map(sub => (
                          <Paper key={sub._id} elevation={0}
                            sx={{
                              display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.5, borderRadius: 1.5, bgcolor: 'background.paper',
                              border: `1px solid ${alpha(theme.palette.divider, 0.8)}`, transition: 'all 0.15s ease',
                              '&:hover': { borderColor: alpha(accent, 0.3), bgcolor: alpha(accent, 0.02) },
                            }}>
                            <SubdirectoryArrowRight sx={{ fontSize: 18, color: alpha(theme.palette.text.secondary, 0.5), flexShrink: 0 }} />
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography variant="body2" fontWeight={500} noWrap>{sub.name}</Typography>
                              {sub.description && <Typography variant="caption" color="text.secondary" noWrap>{sub.description}</Typography>}
                            </Box>
                            {hasWrite && (
                              <Box sx={{ display: 'flex', gap: 0.25 }}>
                                <Tooltip title="Edit">
                                  <IconButton size="small" onClick={() => setSubDialog({ categoryId: cat._id, editing: sub })}
                                    sx={{ color: 'text.secondary', '&:hover': { color: accent, bgcolor: alpha(accent, 0.1) } }}>
                                    <Edit sx={{ fontSize: 16 }} />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Delete">
                                  <IconButton size="small" onClick={() => setDeleteTarget({ type: 'sub', item: sub, parentCatId: cat._id })}
                                    sx={{ color: 'text.secondary', '&:hover': { color: theme.palette.error.main, bgcolor: alpha(theme.palette.error.main, 0.1) } }}>
                                    <Delete sx={{ fontSize: 16 }} />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            )}
                          </Paper>
                        ))}
                      </Stack>
                    ) : (
                      !hasWrite && <Typography variant="body2" color="text.secondary" sx={{ py: 1, fontStyle: 'italic' }}>No sub-categories</Typography>
                    )}
                  </Box>
                </Collapse>
              </Paper>
            );
          })}
        </Stack>
      )}

      <Menu
        anchorEl={menu?.anchor}
        open={!!menu}
        onClose={() => setMenu(null)}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        {...elegantMenuProps}
      >
        <MenuItem
          onClick={() => {
            const t = menu!.target;
            setMenu(null);
            if (t.type === 'category') setCatDialog({ editing: t.item });
            else setSubDialog({ categoryId: t.parentCatId, editing: t.item });
          }}
          sx={{ gap: 1.5, py: 1 }}
        >
          <Edit fontSize="small" sx={{ color: accent }} />
          <Typography variant="body2" fontWeight={500}>Edit</Typography>
        </MenuItem>
        <MenuItem onClick={() => { setDeleteTarget(menu!.target); setMenu(null); }} sx={{ gap: 1.5, py: 1 }}>
          <Delete fontSize="small" sx={{ color: theme.palette.error.main }} />
          <Typography variant="body2" fontWeight={500} color="error.main">Delete</Typography>
        </MenuItem>
      </Menu>

      <CategoryDialog
        open={!!catDialog}
        editing={catDialog?.editing ?? null}
        config={config}
        onClose={() => setCatDialog(null)}
        onSaved={loadCategories}
      />
      <SubCategoryDialog
        open={!!subDialog}
        categoryId={subDialog?.categoryId ?? ''}
        editing={subDialog?.editing ?? null}
        config={config}
        onClose={() => setSubDialog(null)}
        onSaved={loadSubCategories}
      />
      <ConfirmationDialog
        open={!!deleteTarget}
        title={`Delete ${deleteTarget?.type === 'category' ? 'Category' : 'Sub-Category'}`}
        message={`Delete "${deleteTarget?.item.name}"? This cannot be undone. If any ${config.records} use this ${deleteTarget?.type === 'category' ? 'category' : 'sub-category'}, deletion will fail.`}
        confirmText="Delete"
        confirmColor="error"
        onConfirm={handleDelete}
        onClose={() => setDeleteTarget(null)}
      />
    </Box>
  );
};

export default CategoryTreeSettings;
